import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../store/root-reducer';
import { LeadFinderSecondaryTab, LeadRevealType } from '../../types';
import { SAVED_TAB_BULK_OPERATION_LIMIT } from '../../helpers/helper';
import {
  leadFinderBulkOperationLimit,
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
} from '../../../../shared/utils/user-details';
import {
  openCreditNotAvailableModal,
  setShowBulkRevealTagModal,
  setShowSelectedMoreThanLimitModal,
} from '../../v2-lead-slice';

import { AnalyticsEvents } from '../../../../shared/enums/analytics';

interface UseBulkRevealProps {
  handleBulkReveal: (
    revealType: LeadRevealType,
    tagIds: number[],
    newTags: string[],
  ) => Promise<void>;
  leadsData: { profiles: any[]; pagination: any };
  leadCredits: number;
  activeSubTab: LeadFinderSecondaryTab;
}

export const useBulkReveal = (props: UseBulkRevealProps) => {
  const { handleBulkReveal, leadsData, leadCredits, activeSubTab } = props;

  const dispatch = useDispatch();
  const {
    isSelectedAllLeads,
    selectedLeads,
    peopleMaxLeadsLimit,
  } = useSelector((state: RootState) => state.leadFinderV2.selection);

  const [isBulkRevealing, setIsBulkRevealing] = useState(false);
  const [tagsToAdd, setTagsToAdd] = useState<any[]>([]);
  const [leadRevealType, setLeadRevealType] = useState<LeadRevealType | null>(
    null,
  );
  const [bulkRevealEffectiveCount, setBulkRevealEffectiveCount] = useState(0);

  const leadFinderBulkOperationLimitValue = leadFinderBulkOperationLimit();

  // Compute topPeopleLimit inline from leadCredits + leadFinderBulkOperationLimit
  const topPeopleLimit =
    Number(leadCredits) >= leadFinderBulkOperationLimitValue
      ? leadFinderBulkOperationLimitValue
      : Number(leadCredits);

  // Handle bulk reveal with tag modal
  const handleBulkRevealWithTags = useCallback(
    (revealType: LeadRevealType) => {
      // Credit checks for bulk reveal
      if (topPeopleLimit <= 0) {
        dispatch(openCreditNotAvailableModal(revealType));
        return;
      }

      // Calculate count
      let count = 0;
      if (isSelectedAllLeads) {
        // User-applied custom limit takes precedence over the default bulk cap.
        if (peopleMaxLeadsLimit != null) {
          count = peopleMaxLeadsLimit;
        } else {
          count =
            leadsData?.pagination?.totalRows > leadFinderBulkOperationLimitValue
              ? leadFinderBulkOperationLimitValue
              : leadsData?.pagination?.totalRows || 0;
        }
      } else {
        // For manual selection, credit check must use the *selected* count.
        // Using only "unrevealed" leads undercounts and incorrectly allows actions
        // (then opening the tag modal) even when credits are insufficient.
        count = selectedLeads.length;
      }

      setBulkRevealEffectiveCount(count);

      // Check if count exceeds bulk operation limit
      const bulkOperationLimit =
        activeSubTab === LeadFinderSecondaryTab.SAVED
          ? SAVED_TAB_BULK_OPERATION_LIMIT
          : leadFinderBulkOperationLimit();
      if (count > bulkOperationLimit) {
        dispatch(setShowSelectedMoreThanLimitModal(true));
        return;
      }

      // Check credits
      const creditCostPerLead: Record<string, number> = {
        [LeadRevealType.Email]: 1,
        [LeadRevealType.Phone]: 6,
        [LeadRevealType.PersonalEmail]: 2,
        [LeadRevealType.EmailPhone]: 7,
        [LeadRevealType.PersonalEmailPhone]: 8,
        [LeadRevealType.EmailPersonalEmail]: 3,
        [LeadRevealType.EmailPersonalEmailPhone]: 9,
      };
      const costPerLead = creditCostPerLead[revealType] || 1;
      if (Number(leadCredits) < count * costPerLead) {
        dispatch(openCreditNotAvailableModal(revealType));
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }

      // All checks passed, show tag modal
      setLeadRevealType(revealType);
      dispatch(setShowBulkRevealTagModal(true));
    },
    [
      topPeopleLimit,
      isSelectedAllLeads,
      leadsData,
      selectedLeads,
      peopleMaxLeadsLimit,
      leadCredits,
      activeSubTab,
      dispatch,
      leadFinderBulkOperationLimitValue,
    ],
  );

  // Handle bulk reveal submit (from modal)
  const handleBulkRevealSubmit = useCallback(async () => {
    if (!leadRevealType) return;

    setIsBulkRevealing(true);
    const tagIds: number[] = [];
    const newTags: string[] = [];

    tagsToAdd.forEach((tag) => {
      if (tag.isNew) {
        newTags.push(tag.name);
      } else {
        tagIds.push(tag.id);
      }
    });

    await handleBulkReveal(leadRevealType, tagIds, newTags);
    setIsBulkRevealing(false);
    dispatch(setShowBulkRevealTagModal(false));
    setTagsToAdd([]);
    setLeadRevealType(null);
  }, [leadRevealType, tagsToAdd, handleBulkReveal, dispatch]);

  return {
    isBulkRevealing,
    tagsToAdd,
    setTagsToAdd,
    leadRevealType,
    setLeadRevealType,
    bulkRevealEffectiveCount,
    handleBulkRevealWithTags,
    handleBulkRevealSubmit,
  };
};
